<template>
    <b-modal
        id="usage-analysis-modal"
        :visible="show"
        :title="getModalTitle()"
        size="xl"
        hide-footer
        centered
        lazy
        @show="loadUsageAnalysis"
        @hidden="handleHidden"
    >
        <div v-if="loadingUsageAnalysis" class="usage-analysis-loading">
            <i class="fas fa-spinner fa-spin"></i>
            <span>데이터를 불러오는 중...</span>
        </div>
        <div v-else-if="usageAnalysisData" class="usage-analysis-content">
            <!-- 요약 정보 -->
            <div class="usage-analysis-summary">
                <div class="summary-left">
                    <div class="summary-item">
                        <span class="summary-label">계약명:</span>
                        <span class="summary-value">{{ usageAnalysisData.contract?.contractName || "-" }}</span>
                    </div>
                    <div class="summary-item">
                        <span class="summary-label">기간:</span>
                        <span class="summary-value">
                            {{
                                formatDateRange(usageAnalysisData.period?.startDate, usageAnalysisData.period?.endDate)
                            }}
                        </span>
                    </div>
                    <div class="summary-item">
                        <span class="summary-label">단위:</span>
                        <span class="summary-value">{{ usageAnalysisData.unit || "kWh" }}</span>
                    </div>
                    <div class="summary-item">
                        <span class="summary-label">{{ getExtremeValueLabel() }}:</span>
                        <span class="summary-value total">
                            {{ getExtremeValueDisplay() }}
                        </span>
                    </div>
                </div>
                <div class="summary-right">
                    <div class="heatmap-legend">
                        <span class="heatmap-label">낮음</span>
                        <div class="heatmap-gradient" :style="{ background: legendGradient }"></div>
                        <span class="heatmap-label">높음</span>
                    </div>
                    <div class="ebc-segmented">
                        <button
                            v-for="opt in dataTypeOptions"
                            :key="opt.value"
                            type="button"
                            class="ebc-segmented__item"
                            :class="{ active: dataType === opt.value }"
                            @click="setDataType(opt.value)"
                        >
                            {{ opt.label }}
                        </button>
                    </div>
                    <div class="ebc-segmented">
                        <button
                            v-for="opt in sumMethodOptions"
                            :key="opt.value"
                            type="button"
                            class="ebc-segmented__item"
                            :class="{ active: sumMethod === opt.value }"
                            @click="sumMethod = opt.value"
                        >
                            {{ opt.label }}
                        </button>
                    </div>
                    <button type="button" class="btn-excel-download" @click="exportToExcel">엑셀 다운로드</button>
                </div>
            </div>

            <!-- 그리드 테이블 -->
            <div class="usage-grid-container">
                <table ref="gridTable" class="usage-grid-table" @mouseover="onGridOver" @mouseleave="clearHover">
                    <thead>
                        <tr>
                            <th class="hour-header">시간</th>
                            <th
                                v-for="(head, index) in gridView.heads"
                                :key="index"
                                :data-c="index"
                                :class="['date-header', { 'extreme-col': extreme.cols.has(index) }]"
                                :style="{ color: head.color }"
                                :title="head.title"
                            >
                                <span class="date-day">{{ head.day }}</span>
                                <span class="date-dow">{{ head.dow }}</span>
                            </th>
                            <th class="min-header">{{ getSumMethodLabel() }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(row, rowIndex) in gridView.rows"
                            :key="row.hour"
                            :data-r="rowIndex"
                            :class="{ 'extreme-row': extreme.rows.has(rowIndex) }"
                        >
                            <td class="hour-cell">{{ row.hour }}시</td>
                            <td
                                v-for="(cell, colIndex) in row.cells"
                                :key="colIndex"
                                :data-c="colIndex"
                                :class="[
                                    'value-cell',
                                    {
                                        'zero-value': cell.zero,
                                        'extreme-value': extreme.cells.has(rowIndex * colCount + colIndex),
                                    },
                                ]"
                                :style="cell.style"
                            >
                                {{ cell.text }}
                            </td>
                            <td class="min-cell">
                                {{ formatNumber(aggregates.rows[rowIndex], 1) }}
                            </td>
                        </tr>
                        <!-- 일별 집계값 행 -->
                        <tr class="summary-row daily-min-row">
                            <td class="hour-cell agg-label">{{ getSumMethodLabel() }}</td>
                            <td
                                v-for="(value, colIndex) in aggregates.days"
                                :key="`agg-${colIndex}`"
                                :data-c="colIndex"
                                :class="[
                                    'summary-cell',
                                    'daily-min-cell',
                                    { 'extreme-col': extreme.cols.has(colIndex) },
                                ]"
                            >
                                {{ formatNumber(value, 1) }}
                            </td>
                            <td class="summary-cell min-total-cell">
                                {{ formatNumber(aggregates.grand, 1) }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
        <div v-else class="usage-analysis-empty">
            <i class="fas fa-exclamation-circle"></i>
            <span>데이터를 불러올 수 없습니다.</span>
        </div>
    </b-modal>
</template>

<script>
import backEndApi from "@/api/backEndApi";
import * as XLSX from "xlsx";
import { navyScale } from "../../utils/chartTheme.js";
import { dayLabelColor } from "../analysis/analysisChart.js";

const AGG_LABELS = { Sum: "합계", Avg: "평균", Max: "최대", Min: "최소" };
const oneDecimalFormat = new Intl.NumberFormat("ko-KR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
});
const HEAT_STOPS = ["#f4f8fd", navyScale[4], navyScale[3], navyScale[2], navyScale[1]];
const HEAT_RGB = HEAT_STOPS.map((hex) => [1, 3, 5].map((i) => parseInt(hex.substring(i, i + 2), 16)));
const DATA_TYPE_OPTIONS = [
    { value: "USAGE", label: "사용량" },
    { value: "PEAK", label: "피크" },
];
const SUM_METHOD_OPTIONS = Object.entries(AGG_LABELS).map(([value, label]) => ({ value, label }));

const round2 = (v) => Math.round(v * 100) / 100;

/** values 는 데이터가 있는 칸만 (결측 제외, 실제 0 포함) */
function aggregate(values, method) {
    if (!values.length) return 0;
    switch (method) {
        case "Min":
            return round2(Math.min(...values));
        case "Max":
            return round2(Math.max(...values));
        case "Avg":
            return round2(values.reduce((sum, v) => sum + v, 0) / values.length);
        default:
            return round2(values.reduce((sum, v) => sum + v, 0));
    }
}

function heatmapStyle(value, min, range) {
    if (!value || range === 0) return null;
    const ratio = (value - min) / range;
    const pos = ratio * (HEAT_RGB.length - 1);
    const i = Math.min(Math.floor(pos), HEAT_RGB.length - 2);
    const [from, to] = [HEAT_RGB[i], HEAT_RGB[i + 1]];
    const rgb = from.map((c, k) => Math.round(c + (to[k] - c) * (pos - i)));
    return {
        backgroundColor: `rgb(${rgb.join(", ")})`,
        color: ratio > 0.7 ? "#ffffff" : "#1e293b",
    };
}

export default {
    name: "UsageAnalysisModal",
    props: {
        show: {
            type: Boolean,
            default: false,
        },
        contractIdx: {
            type: Number,
            required: true,
        },
        currentDate: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            usageAnalysisData: null,
            loadingUsageAnalysis: false,
            dataType: "USAGE", // 기본값: 사용량
            sumMethod: "Sum", // 기본값: 합계
            dataTypeOptions: DATA_TYPE_OPTIONS,
            legendGradient: `linear-gradient(to right, ${HEAT_STOPS.join(", ")})`,
        };
    },
    computed: {
        colCount() {
            return this.usageAnalysisData?.columns?.length || 0;
        },
        // 피크(kW)는 시간별 최대를 더한 값이 의미가 없어 합계를 뺀다
        sumMethodOptions() {
            return this.dataType === "PEAK" ? SUM_METHOD_OPTIONS.filter((o) => o.value !== "Sum") : SUM_METHOD_OPTIONS;
        },
        // present[r][c]: 데이터가 있는 칸. missing 이 없는 응답(구 서버)은 0 을 결측으로 본다
        present() {
            const grid = this.usageAnalysisData?.grid;
            if (!grid) return [];
            return grid.map((row) => {
                if (!Array.isArray(row.missing)) return row.data.map((v) => v > 0);
                const missing = new Set(row.missing);
                return row.data.map((_, c) => !missing.has(c));
            });
        },
        gridView() {
            const data = this.usageAnalysisData;
            if (!data?.grid) return { heads: [], rows: [] };

            const columns = data.columns || [];
            let max = 0;
            let min = Infinity;
            data.grid.forEach((row) =>
                row.data.forEach((v) => {
                    if (v > max) max = v;
                    if (v > 0 && v < min) min = v;
                }),
            );
            if (min === Infinity) min = 0;

            return {
                heads: columns.map((c) => {
                    const iso = `${c.date.substring(0, 4)}-${c.date.substring(4, 6)}-${c.date.substring(6, 8)}`;
                    return {
                        label: `${c.month}/${c.day}(${c.dayOfWeek})`,
                        day: c.day,
                        dow: c.dayOfWeek,
                        color: dayLabelColor(iso, c.isHoliday),
                        title: `${iso} (${c.dayOfWeek}${c.isHoliday ? "·공휴일" : ""})`,
                    };
                }),
                rows: data.grid.map((row, r) => ({
                    hour: row.hour,
                    cells: row.data.map((v, c) => {
                        const has = this.present[r][c];
                        return {
                            text: has ? this.formatNumber(v, 1) : "-",
                            zero: !has,
                            style: has ? heatmapStyle(v, min, max - min) : null,
                        };
                    }),
                })),
            };
        },
        // 합계·평균·최대는 최댓값, 최소는 최솟값 셀 (결측 제외). 같은 값이 여러 칸이면 모두 강조
        extreme() {
            const result = {
                cells: new Set(),
                rows: new Set(),
                cols: new Set(),
                first: null,
                value: null,
            };
            const grid = this.usageAnalysisData?.grid;
            if (!grid) return result;

            const findMax = this.sumMethod !== "Min";
            grid.forEach((row, r) =>
                row.data.forEach((v, c) => {
                    if (!this.present[r][c]) return;
                    if (result.value === null || (findMax ? v > result.value : v < result.value)) {
                        result.value = v;
                    }
                }),
            );
            if (result.value === null) return result;

            grid.forEach((row, r) =>
                row.data.forEach((v, c) => {
                    if (v !== result.value || !this.present[r][c]) return;
                    result.cells.add(r * this.colCount + c);
                    result.rows.add(r);
                    result.cols.add(c);
                    if (!result.first) result.first = { row: r, col: c };
                }),
            );
            return result;
        },
        // 서버(getHourlyUsageGrid)와 같은 규칙: 데이터 있는 칸 기준, 전체 합계는 일별 합계의 합, 소수 둘째 자리 반올림
        aggregates() {
            const grid = this.usageAnalysisData?.grid;
            if (!grid) return { rows: [], days: [], grand: 0 };

            const method = this.sumMethod;
            const present = this.present;
            const rowValues = grid.map((row, r) => row.data.filter((_, c) => present[r][c]));
            const rows = rowValues.map((values) => aggregate(values, method));
            const days = Array.from({ length: this.colCount }, (_, c) =>
                aggregate(
                    grid.filter((_, r) => present[r][c]).map((row) => row.data[c]),
                    method,
                ),
            );
            const grand =
                method === "Sum" ? round2(days.reduce((sum, v) => sum + v, 0)) : aggregate(rowValues.flat(), method);
            return { rows, days, grand };
        },
    },
    created() {
        this.responseCache = new Map();
        this.requestSeq = 0;
        this.hoverCell = null;
        this.hoverEls = [];
    },
    methods: {
        async loadUsageAnalysis() {
            if (!this.contractIdx) {
                this.$bvToast.toast("계약 정보가 없습니다.", {
                    title: "오류",
                    variant: "danger",
                    solid: true,
                });
                return;
            }

            this.clearHover();
            const key = `${this.contractIdx}|${this.currentDate}|${this.dataType}`;
            if (this.responseCache.has(key)) {
                this.usageAnalysisData = this.responseCache.get(key);
                return;
            }

            const seq = ++this.requestSeq;
            this.loadingUsageAnalysis = true;
            this.usageAnalysisData = null;

            try {
                const response = await backEndApi.energyBillCalc.analyzeUsage({
                    contractIdx: this.contractIdx,
                    type: this.dataType, // USAGE 또는 PEAK
                    date: this.currentDate,
                });
                if (seq !== this.requestSeq) return;
                if (!response?.data) throw new Error("데이터를 불러올 수 없습니다.");

                const data = Object.freeze(response.data);
                this.responseCache.set(key, data);
                this.usageAnalysisData = data;
            } catch (error) {
                if (seq !== this.requestSeq) return;
                console.error("Failed to load usage analysis:", error);
                this.$bvToast.toast("사용량 분석 데이터를 불러오는데 실패했습니다.", {
                    title: "오류",
                    variant: "danger",
                    solid: true,
                });
                this.usageAnalysisData = null;
            } finally {
                if (seq === this.requestSeq) this.loadingUsageAnalysis = false;
            }
        },
        formatDateRange(startDate, endDate) {
            if (!startDate || !endDate) return "-";
            const formatDate = (dateStr) => {
                if (dateStr.length === 8) {
                    return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
                }
                return dateStr;
            };
            return `${formatDate(String(startDate))} ~ ${formatDate(String(endDate))}`;
        },
        formatNumber(value, decimals = 0) {
            if (value === null || value === undefined || isNaN(value)) return "0";
            if (decimals === 1) return oneDecimalFormat.format(value);
            return Number(value).toLocaleString("ko-KR", {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
            });
        },
        handleHidden() {
            this.clearHover();
            this.requestSeq++;
            this.responseCache.clear();
            this.usageAnalysisData = null;
            this.loadingUsageAnalysis = false;
            this.$emit("update:show", false);
        },
        // 셀마다 반응형 상태를 바꾸면 표 전체가 다시 그려지므로 강조 클래스는 DOM에서 직접 토글
        onGridOver(event) {
            const cell = event.target.closest("td");
            if (cell === this.hoverCell) return;
            this.clearHover();
            if (!cell || cell.classList.contains("hour-cell")) return;

            this.hoverCell = cell;
            const targets = [];
            if (cell.parentElement.dataset.r !== undefined) {
                targets.push(cell.parentElement.querySelector(".hour-cell"));
            }
            if (cell.dataset.c !== undefined) {
                targets.push(this.$refs.gridTable?.querySelector(`thead th[data-c="${cell.dataset.c}"]`));
            }
            this.hoverEls = targets.filter(Boolean);
            this.hoverEls.forEach((el) => el.classList.add("header-highlighted"));
        },
        clearHover() {
            this.hoverEls.forEach((el) => el.classList.remove("header-highlighted"));
            this.hoverEls = [];
            this.hoverCell = null;
        },
        getOverallMin() {
            const values = (this.usageAnalysisData?.grid || []).flatMap((row, r) =>
                row.data.filter((_, c) => this.present[r][c]),
            );
            return aggregate(values, "Min");
        },
        setDataType(type) {
            if (type === this.dataType) return;
            this.dataType = type;
            if (type === "PEAK" && this.sumMethod === "Sum") this.sumMethod = "Max";
            this.loadUsageAnalysis();
        },
        getSumMethodLabel() {
            return AGG_LABELS[this.sumMethod] || "합계";
        },
        getDataTypeLabel() {
            return this.dataType === "PEAK" ? "피크" : "사용량";
        },
        getModalTitle() {
            return `데이터 분석 (${this.getDataTypeLabel()})`;
        },
        getExtremeValueLabel() {
            return this.sumMethod === "Min" ? "최소구간" : "최대구간";
        },
        getExtremeValueDisplay() {
            const data = this.usageAnalysisData;
            const unit = data?.unit || "kWh";
            const first = this.extreme.first;
            if (!first) return `${this.formatNumber(this.aggregates.grand, 1)} ${unit}`;

            const head = this.gridView.heads[first.col];
            const hour = data.grid[first.row].hour;
            return `${head ? head.label : ""} ${hour}시 (${this.formatNumber(this.extreme.value, 1)} ${unit})`;
        },
        exportToExcel() {
            if (!this.usageAnalysisData) {
                this.$bvToast.toast("다운로드할 데이터가 없습니다.", {
                    title: "오류",
                    variant: "danger",
                    solid: true,
                });
                return;
            }

            try {
                const data = this.usageAnalysisData;
                const excelData = [];

                // 헤더 행 생성
                const headerRow = ["시간"];
                if (data.columns && data.columns.length > 0) {
                    data.columns.forEach((column) => {
                        headerRow.push(`${column.month}/${column.day}(${column.dayOfWeek})`);
                    });
                }
                const aggLabel = this.getSumMethodLabel();
                headerRow.push(aggLabel, "최소");
                excelData.push(headerRow);

                // 시간대별 데이터 행
                if (data.grid && data.grid.length > 0) {
                    data.grid.forEach((gridItem, rowIndex) => {
                        const row = [`${gridItem.hour}시`];
                        if (gridItem.data && gridItem.data.length > 0) {
                            gridItem.data.forEach((value, colIndex) => {
                                row.push(this.present[rowIndex][colIndex] ? Number(value) : "-");
                            });
                        }
                        row.push(
                            this.aggregates.rows[rowIndex],
                            aggregate(
                                gridItem.data.filter((_, colIndex) => this.present[rowIndex][colIndex]),
                                "Min",
                            ),
                        );
                        excelData.push(row);
                    });
                }

                // 일별 집계 행
                if (data.grid) {
                    excelData.push([`일별 ${aggLabel}`, ...this.aggregates.days, this.aggregates.grand, "-"]);
                }

                // 일별 최소값 행
                if (data.summary && data.summary.dailyMinByHour) {
                    const minRow = ["일별 최소"];
                    data.summary.dailyMinByHour.forEach((min) => {
                        minRow.push(Number(min));
                    });
                    minRow.push("-", Number(this.getOverallMin()));
                    excelData.push(minRow);
                }

                // 워크시트 생성
                const worksheet = XLSX.utils.aoa_to_sheet(excelData);

                // 컬럼 너비 설정
                const columnWidths = [{ wch: 10 }]; // 시간 컬럼
                if (data.columns) {
                    data.columns.forEach(() => {
                        columnWidths.push({ wch: 12 }); // 날짜 컬럼들
                    });
                }
                columnWidths.push({ wch: 12 }, { wch: 12 }); // 합계, 최소 컬럼
                worksheet["!cols"] = columnWidths;

                // 워크북 생성
                const workbook = XLSX.utils.book_new();
                const contractName = data.contract?.contractName || "사용량분석";
                const sheetName = contractName.length > 31 ? contractName.substring(0, 31) : contractName;
                XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

                // 파일명 생성
                const today = new Date();
                const dateStr = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, "0")}${String(today.getDate()).padStart(2, "0")}`;
                const fileName = `사용량분석_${contractName}_${dateStr}.xlsx`;

                // 엑셀 파일 다운로드
                XLSX.writeFile(workbook, fileName);

                this.$bvToast.toast("엑셀 파일이 다운로드되었습니다.", {
                    title: "완료",
                    variant: "success",
                    solid: true,
                });
            } catch (error) {
                console.error("Failed to export to Excel:", error);
                this.$bvToast.toast("엑셀 다운로드 중 오류가 발생했습니다.", {
                    title: "오류",
                    variant: "danger",
                    solid: true,
                });
            }
        },
    },
};
</script>

<style scoped>
::v-deep #usage-analysis-modal .modal-dialog {
    width: 98% !important;
    max-width: 98% !important;
}

::v-deep #usage-analysis-modal .modal-content {
    max-height: 95vh;
    border: none;
    border-radius: var(--ebc-radius-md);
}

::v-deep #usage-analysis-modal .modal-header {
    padding: 14px 20px;
    border-bottom: 1px solid var(--ebc-hairline-strong);
}

::v-deep #usage-analysis-modal .modal-title {
    font-size: 16px;
    font-weight: 700;
    color: var(--ebc-text);
}

.usage-analysis-loading,
.usage-analysis-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    height: 85vh;
    color: var(--ebc-text-sub);
}

.usage-analysis-loading i {
    font-size: 24px;
    color: var(--ebc-navy-2);
}

.usage-analysis-empty i {
    font-size: 24px;
    color: var(--ebc-text-muted);
}

.usage-analysis-content {
    display: flex;
    flex-direction: column;
    gap: 12px;
    height: 85vh;
}

/* 요약 + 컨트롤 */
.usage-analysis-summary {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 10px 16px;
}

.summary-left {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 20px;
    flex: 1;
}

.summary-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
}

.summary-label {
    color: var(--ebc-text-sub);
    font-weight: 500;
}

.summary-value {
    color: var(--ebc-text);
    font-weight: 700;
    font-variant-numeric: tabular-nums;
}

.summary-right {
    display: flex;
    align-items: center;
    gap: 8px;
}

.summary-right .ebc-segmented__item {
    padding: 4px 12px;
    font-size: 12px;
}

.summary-right .ebc-segmented__item.active {
    color: var(--ebc-navy-1);
}

.heatmap-legend {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-right: 4px;
}

.heatmap-gradient {
    width: 96px;
    height: 8px;
    border-radius: var(--ebc-radius-pill);
}

.heatmap-label {
    font-size: 11px;
    color: var(--ebc-text-muted);
}

.btn-excel-download {
    padding: 5px 12px;
    font-size: 12px;
    font-weight: 600;
    color: var(--ebc-text-sub);
    background: var(--ebc-surface);
    border: 1px solid var(--ebc-gray-5);
    border-radius: var(--ebc-radius-pill);
    cursor: pointer;
}

.btn-excel-download:hover {
    color: var(--ebc-text);
    border-color: var(--ebc-gray-4);
}

.btn-excel-download:focus {
    outline: none;
}

/* 그리드 */
.usage-grid-container {
    flex: 1;
    min-height: 0;
    overflow: auto;
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-sm);
}

.usage-grid-table {
    width: 100%;
    height: 100%;
    table-layout: fixed;
    border-collapse: separate;
    border-spacing: 0;
    background: var(--ebc-surface);
    font-variant-numeric: tabular-nums;
}

.usage-grid-table th {
    position: sticky;
    top: 0;
    z-index: 3;
    padding: 6px 2px;
    text-align: center;
    line-height: 1.25;
    color: var(--ebc-text-sub);
    font-size: 11px;
    font-weight: 600;
    background: var(--ebc-surface-sunken);
    border-bottom: 1px solid var(--ebc-gray-5);
    white-space: nowrap;
}

.date-day {
    display: block;
    font-size: 12px;
    font-weight: 700;
}

.date-dow {
    display: block;
    font-size: 10px;
    font-weight: 500;
    opacity: 0.8;
}

.hour-header {
    left: 0;
    z-index: 5 !important;
    width: 44px;
}

.min-header {
    right: 0;
    z-index: 5 !important;
    width: 64px;
    border-left: 1px solid var(--ebc-gray-5);
}

.usage-grid-table td {
    padding: 3px 5px;
    text-align: right;
    font-size: 11px;
    color: var(--ebc-text);
    border-right: 1px solid var(--ebc-surface);
    border-bottom: 1px solid var(--ebc-surface);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.hour-cell {
    position: sticky;
    left: 0;
    z-index: 2;
    text-align: center !important;
    color: var(--ebc-text-sub) !important;
    font-weight: 600;
    background: var(--ebc-surface-sunken);
    border-right: 1px solid var(--ebc-gray-5) !important;
}

.value-cell {
    cursor: default;
}

.value-cell.zero-value {
    color: var(--ebc-text-muted);
    background: var(--ebc-surface-sunken);
}

.min-cell {
    position: sticky;
    right: 0;
    z-index: 2;
    font-weight: 700;
    background: var(--ebc-track);
    border-left: 1px solid var(--ebc-gray-5);
}

/* 집계 행 */
.summary-row td {
    position: sticky;
    bottom: 0;
    z-index: 2;
    font-weight: 700;
    background: var(--ebc-track);
    border-top: 1px solid var(--ebc-gray-5);
}

.summary-row td.agg-label {
    left: 0;
    z-index: 4;
}

.summary-row td.min-total-cell {
    right: 0;
    z-index: 4;
    border-left: 1px solid var(--ebc-gray-5);
}

/* hover 시 해당 시간·날짜 머리글 */
.header-highlighted {
    color: var(--ebc-navy-1) !important;
    background: var(--ebc-navy-5) !important;
}

/* 극단값: 셀은 안쪽 외곽선, 행·열은 머리글만 강조 */
.usage-grid-table td.extreme-value {
    font-weight: 700;
    box-shadow: inset 0 0 0 2px var(--ebc-navy-1);
}

.usage-grid-table th.extreme-col {
    box-shadow: inset 0 -2px 0 var(--ebc-navy-1);
}

.usage-grid-table tr.extreme-row td.hour-cell {
    color: var(--ebc-navy-1) !important;
    box-shadow: inset -2px 0 0 var(--ebc-navy-1);
}

.usage-grid-table td.summary-cell.extreme-col {
    color: var(--ebc-navy-1);
    box-shadow: inset 0 2px 0 var(--ebc-navy-1);
}
</style>
